/**
 * Theme Palette Configuration
 * Defines the Mensova dark-mode-first color system.
 */

import { PaletteOptions } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    surface2: string;
    surface3: string;
    border: {
      default: string;
      subtle: string;
      strong: string;
    };
    accent: {
      main: string;
      hover: string;
      pressed: string;
      glow: string;
      border: string;
    };
  }
  interface PaletteOptions {
    surface2?: string;
    surface3?: string;
    border?: {
      default: string;
      subtle: string;
      strong: string;
    };
    accent?: {
      main: string;
      hover: string;
      pressed: string;
      glow: string;
      border: string;
    };
  }
}

const primary = {
  main: '#6F91B3',
  light: '#8AA8C6',
  dark: '#5E748A',
  contrastText: '#0D1218',
};

const secondary = {
  main: '#B08D57',
  light: '#C6A26B',
  dark: '#8A6B3F',
  contrastText: '#0D1218',
};

const success = {
  main: '#5E8C6A',
  light: '#7C9A74',
  dark: '#476D52',
  contrastText: '#0D1218',
};

const warning = {
  main: '#B08D57',
  light: '#C6A26B',
  dark: '#8A6B3F',
  contrastText: '#0D1218',
};

const error = {
  main: '#B66B5B',
  light: '#CC8171',
  dark: '#8F5146',
  contrastText: '#0D1218',
};

const info = {
  main: '#6F91B3',
  light: '#8A98A8',
  dark: '#5E748A',
  contrastText: '#0D1218',
};

export const darkPalette: PaletteOptions = {
  mode: 'dark',

  primary,
  secondary,
  success,
  warning,
  error,
  info,

  surface2: '#1A2734',
  surface3: '#223142',

  border: {
    subtle: 'rgba(247,244,238,0.10)',
    default: 'rgba(247,244,238,0.16)',
    strong: 'rgba(176,141,87,0.34)',
  },

  accent: {
    main: '#B08D57',
    hover: '#C6A26B',
    pressed: '#8A6B3F',
    glow: 'rgba(176,141,87,0.12)',
    border: 'rgba(176,141,87,0.34)',
  },

  background: {
    default: '#0D1218',
    paper: '#131D27',
  },

  text: {
    primary: '#F2EEE7',
    secondary: '#B8B0A4',
    disabled: '#7E7A73',
  },

  divider: 'rgba(247,244,238,0.12)',

  action: {
    active: '#F2EEE7',
    hover: 'rgba(247,244,238,0.06)',
    selected: 'rgba(176,141,87,0.14)',
    disabled: 'rgba(242,238,231,0.34)',
    disabledBackground: 'rgba(242,238,231,0.08)',
    focus: 'rgba(176,141,87,0.28)',
  },

  grey: {
    50: '#F7F4EE',
    100: '#F2EEE7',
    200: '#D9D1C5',
    300: '#B8B0A4',
    400: '#8A98A8',
    500: '#7E7A73',
    600: '#4C5560',
    700: '#30465E',
    800: '#1D2B3A',
    900: '#0D1218',
  },
};

export const scoreColors = {
  expert: '#5E8C6A',
  expertBg: 'rgba(94, 140, 106, 0.14)',
  proficient: '#7C9A74',
  proficientBg: 'rgba(124, 154, 116, 0.14)',
  competent: '#B08D57',
  competentBg: 'rgba(176, 141, 87, 0.14)',
  developing: '#9B8A68',
  developingBg: 'rgba(155, 138, 104, 0.14)',
  novice: '#7A746B',
  noviceBg: 'rgba(122, 116, 107, 0.14)',
} as const;

export const statusColors = {
  success: '#5E8C6A',
  successBg: 'rgba(94, 140, 106, 0.14)',
  warning: '#B08D57',
  warningBg: 'rgba(176, 141, 87, 0.14)',
  error: '#B66B5B',
  errorBg: 'rgba(182, 107, 91, 0.14)',
  info: '#6F91B3',
  infoBg: 'rgba(111, 145, 179, 0.14)',
} as const;

export const trendColors = {
  improving: '#7C9A74',
  stable: '#8A98A8',
  declining: '#B66B5B',
} as const;
